import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, X, AlertTriangle, CheckCircle, DollarSign, MapPin, Navigation, Phone, MessageCircle, Calendar, Plus, Trash2, Clock, Globe, ShieldCheck, Camera, Image as ImageIcon, RotateCcw, Link as LinkIcon, Map as MapIcon, Loader2 } from 'lucide-react';
import { diagnoseIssue } from '../services/geminiService';
import { AIDiagnosis, Shop, LocationData, Language } from '../types';
import { useTranslation } from '../LanguageContext';

interface AIDiagnosticModalProps {
  isOpen: boolean;
  onClose: () => void;
  shop: Shop;
  onConfirmBooking: (data: {
    description: string;
    diagnosis: AIDiagnosis | undefined;
    location: LocationData;
    contactMethod: 'Phone' | 'Telegram';
    contactValue: string;
    preferredSlots: string[];
  }) => void;
}

export const AIDiagnosticModal: React.FC<AIDiagnosticModalProps> = ({ isOpen, onClose, shop, onConfirmBooking }) => {
  const { language } = useTranslation();
  const [description, setDescription] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [diagnosis, setDiagnosis] = useState<AIDiagnosis | null>(null);
  
  const [locationMode, setLocationMode] = useState<'GPS' | 'URL'>('GPS');
  const [location, setLocation] = useState<LocationData | null>(null);
  const [mapUrlInput, setMapUrlInput] = useState('');
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);

  const [contactMethod, setContactMethod] = useState<'Phone' | 'Telegram'>('Phone');
  const [contactValue, setContactValue] = useState('');
  const [preferredSlots, setPreferredSlots] = useState<string[]>(['']);
  const [photo, setPhoto] = useState<string | null>(null);
  
  const modalContentRef = useRef<HTMLDivElement>(null);

  const texts: Record<string, Record<Language, string>> = {
    title: { EN: 'Book Repair', KH: 'កក់ការជួសជុល', ZH: '预约维修', JA: '修理予約', KO: '수리 예약' },
    verified: { EN: 'AI Verified Appointment', KH: 'ការណាត់ជួបដែលផ្ទៀងផ្ទាត់ដោយ AI', ZH: 'AI 验证预约', JA: 'AI 認証済み予約', KO: 'AI 인증 예약' },
    describe: { EN: 'Describe the issue', KH: 'រៀបរាប់ពីបញ្ហា', ZH: '描述问题', JA: '問題を説明する', KO: '문제 설명' },
    step1: { EN: 'Step 1 of 2', KH: 'ជំហានទី ១ នៃ ២', ZH: '第 1 步，共 2 步', JA: 'ステップ 1 / 2', KO: '1/2 단계' },
    placeholder: { EN: 'Describe what is broken... (e.g. My AC makes a loud noise)', KH: 'សូមរៀបរាប់ពីអ្វីដែលខូច... (ឧទាហរណ៍៖ ម៉ាស៊ីនត្រជាក់របស់ខ្ញុំមានសម្លេងលឺខ្លាំង)', ZH: '描述损坏情况...（例如：我的空调噪音很大）', JA: '故障状況を説明してください...（例：エアコンから大きな音がする）', KO: '고장 내용을 설명하세요... (예: 에어컨에서 큰 소음이 납니다)' },
    optional_photo: { EN: 'Optional: Take photo for AI analysis', KH: 'ជម្រើស៖ ថតរូបភាពបង្ហាញភស្តុតាងឱ្យ AI ពិនិត្យ', ZH: '可选：拍照进行 AI 分析', JA: '任意：AI 分析用に写真を撮る', KO: '선택 사항: AI 분석을 위한 사진 촬영' },
    click_photo: { EN: 'Click to capture or upload (AI will analyze)', KH: 'ចុចទីនេះដើម្បីថតរូប ឬផ្ទុកឡើង (AI នឹងវិភាគ)', ZH: '点击拍摄或上传（AI 将进行分析）', JA: 'クリックして撮影またはアップロード（AI が分析します）', KO: '촬영 또는 업로드하려면 클릭 (AI가 분석함)' },
    analyzing: { EN: 'Consulting AI...', KH: 'កំពុងពិគ្រោះជាមួយ AI...', ZH: '咨询 AI 中...', JA: 'AI に相談中...', KO: 'AI와 상담 중...' },
    analyze_btn: { EN: 'Analyze Issue with AI', KH: 'វិភាគបញ្ហាដោយ AI', ZH: '使用 AI 分析问题', JA: 'AI で問題を分析する', KO: 'AI로 문제 분석' },
    tech_view: { EN: 'Technical Insight', KH: 'ការយល់ឃើញបច្ចេកទេស', ZH: '技术洞察', JA: '技術的見解', KO: '기술적 분석' },
    estimate: { EN: 'Estimated Price Range', KH: 'តម្លៃប៉ាន់ស្មាន', ZH: '预估价格范围', JA: '見積价格範囲', KO: '예상 가격 범위' },
    location: { EN: 'Repair Location', KH: 'ទីតាំងជួសជុល', ZH: '维修位置', JA: '修理場所', KO: '수리 위치' },
    use_gps: { EN: 'Use GPS', KH: 'ប្រើ GPS', ZH: '使用 GPS', JA: 'GPS を使用', KO: 'GPS 사용' },
    paste_url: { EN: 'Paste URL', KH: 'ដាក់តំណភ្ជាប់', ZH: '粘贴链接', JA: 'URL を貼り付け', KO: 'URL 붙여넣기' },
    finding_signal: { EN: 'Finding signal...', KH: 'កំពុងស្វែងរកសញ្ញា...', ZH: '正在寻找信号...', JA: '信号を検索中...', KO: '신호를 찾는 중...' },
    set_gps: { EN: 'Set GPS Location', KH: 'កំណត់ទីតាំង GPS', ZH: '设置 GPS 位置', JA: 'GPS 位置を設定', KO: 'GPS 위치 설정' },
    map_placeholder: { EN: 'Paste Google Maps link here...', KH: 'ចម្លងតំណភ្ជាប់ Google Maps នៅទីនេះ...', ZH: '在此粘贴 Google 地图链接...', JA: 'Google マップのリンクをここに貼り付け...', KO: '여기에 Google 지도 링크를 붙여넣으세요...' },
    verify_link: { EN: 'Verify Link', KH: 'ផ្ទៀងផ្ទាត់តំណភ្ជាប់', ZH: '验证链接', JA: 'リンクを検証', KO: '링크 확인' },
    contact: { EN: 'Contact Method', KH: 'វិធីសាស្រ្តទំនាក់ទំនង', ZH: '联系方式', JA: '連絡方法', KO: '연락 方法' },
    phone: { EN: 'Phone', KH: 'ទូរស័ព្ទ', ZH: '电话', JA: '電話', KO: '전화' },
    time: { EN: 'Preferred Times', KH: 'ពេលវេលាដែលចង់បាន', ZH: '首选时间', JA: '希望時間', KO: '선호 시간' },
    add_slot: { EN: 'Add', KH: 'បន្ថែម', ZH: '添加', JA: '追加', KO: '추가' },
    confirm: { EN: 'Confirm Booking', KH: 'បញ្ជាក់ការកក់ជួសជុល', ZH: '确认预约', JA: '予約を確定する', KO: '예약 확정' },
    back: { EN: 'Back to description', KH: 'ត្រឡប់ទៅការរៀបរាប់វិញ', ZH: '返回描述', JA: '説明に戻る', KO: '説明으로 돌아가기' },
    gps_error: { EN: 'GPS not supported', KH: 'មិនគាំទ្រប្រព័ន្ធ GPS', ZH: '不支持 GPS', JA: 'GPS がサポートされていません', KO: 'GPS가 지원되지 않음' },
    perm_denied: { EN: 'Permission denied', KH: 'ការអនុញ្ញាតត្រូវបានបដិសេធ', ZH: '权限被拒绝', JA: '権限が拒否されました', KO: '권한 거부됨' },
    invalid_map: { EN: 'Invalid Google Maps link', KH: 'តំណភ្ជាប់ផែនទីមិនត្រឹមត្រូវ', ZH: '无效的 Google 地图链接', JA: '無効な Google マップリンク', KO: '잘못된 Google 지도 링크' }
  };

  useEffect(() => {
    if (diagnosis && modalContentRef.current) {
      modalContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [diagnosis]);

  if (!isOpen) return null;

  const handleAnalyze = async () => {
    if (!description.trim()) return;
    setIsAnalyzing(true);
    const result = await diagnoseIssue(description, language, photo || undefined);
    setDiagnosis(result);
    setIsAnalyzing(false);
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => setPhoto(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleGetLocation = () => {
    setIsGettingLocation(true);
    setLocationError(null);
    if (!navigator.geolocation) {
      setLocationError(texts.gps_error[language]);
      setIsGettingLocation(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setIsGettingLocation(false);
      },
      () => {
        setLocationError(texts.perm_denied[language]);
        setIsGettingLocation(false);
      }
    );
  };

  const handleUrlSubmit = () => {
    if (!mapUrlInput.trim().includes('google.com/maps') && !mapUrlInput.trim().includes('maps.app.goo.gl')) {
      setLocationError(texts.invalid_map[language]);
      return;
    }
    setLocation({ mapUrl: mapUrlInput.trim() });
    setLocationError(null);
  };

  const addSlot = () => setPreferredSlots([...preferredSlots, '']);
  const removeSlot = (i: number) => setPreferredSlots(preferredSlots.filter((_, idx) => idx !== i));
  const updateSlot = (i: number, val: string) => {
    const next = [...preferredSlots];
    next[i] = val;
    setPreferredSlots(next);
  };

  const isFormReady = description.trim() && location && contactValue.trim() && preferredSlots.some(s => s);

  return (
    <div className="fixed inset-0 z-[60] flex flex-col items-center bg-slate-900/60 backdrop-blur-md overflow-y-auto pt-4 pb-20 px-4 md:pt-12 md:pb-12">
      <div className="bg-white rounded-[3rem] w-full max-w-2xl shadow-2xl flex flex-col animate-in fade-in zoom-in duration-300 border border-slate-100 relative">
        <div className="p-8 border-b border-slate-50 flex justify-between items-center bg-white rounded-t-[3rem] sticky top-0 z-20">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-violet-600 rounded-2xl flex items-center justify-center text-white shadow-xl shadow-violet-200">
              <Sparkles size={28} fill="currentColor" />
            </div>
            <div>
              <h2 className="text-2xl font-black text-slate-900 tracking-tight leading-none mb-1">{texts.title[language]}</h2>
              <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest flex items-center gap-1.5">
                <ShieldCheck size={12} className="text-emerald-500" /> {texts.verified[language]}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="w-12 h-12 rounded-2xl bg-slate-50 flex items-center justify-center text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-all"><X size={24} /></button>
        </div>

        <div ref={modalContentRef} className="p-10 space-y-10">
          {!diagnosis ? (
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between px-1">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    {texts.describe[language]} <span className="text-rose-500 ml-0.5">*</span>
                  </label>
                  <span className="text-[10px] font-bold text-violet-600">{texts.step1[language]}</span>
                </div>
                <div className="relative group">
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={texts.placeholder[language]}
                    className="w-full p-8 rounded-[2.5rem] bg-slate-50 border-2 border-transparent outline-none focus:border-violet-100 focus:bg-white focus:ring-4 focus:ring-violet-50 transition-all min-h-[160px] text-slate-800 font-bold placeholder:text-slate-300 shadow-inner text-lg leading-relaxed"
                  />
                  <div className="absolute bottom-6 right-8 text-slate-300 group-focus-within:text-violet-300 transition-colors"><Sparkles size={20} /></div>
                </div>
              </div>

              <div className="space-y-4">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2"><Camera size={14} className="text-violet-600" /> {texts.optional_photo[language]}</label>
                {!photo ? (
                  <div className="relative h-48 rounded-[2.5rem] bg-slate-50 border-2 border-dashed border-slate-200 flex flex-col items-center justify-center group hover:bg-slate-100 hover:border-violet-200 transition-all duration-300">
                    <ImageIcon size={40} className="text-slate-200 mb-4" />
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest text-center px-8">{texts.click_photo[language]}</p>
                    <input type="file" accept="image/*" capture="environment" onChange={handlePhotoUpload} className="absolute inset-0 opacity-0 cursor-pointer" />
                  </div>
                ) : (
                  <div className="relative h-64 rounded-[2.5rem] overflow-hidden group shadow-2xl border-4 border-white">
                    <img src={photo} className="w-full h-full object-cover" alt="Visual Evidence" />
                    <div className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
                      <button type="button" onClick={() => setPhoto(null)} className="w-12 h-12 rounded-2xl bg-white text-red-500 flex items-center justify-center shadow-xl hover:scale-110 transition-transform"><Trash2 size={20} /></button>
                    </div>
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={handleAnalyze}
                disabled={isAnalyzing || !description.trim()}
                className="w-full py-8 bg-slate-900 text-white rounded-[2.5rem] font-black text-xl hover:bg-violet-600 transition-all shadow-2xl shadow-slate-900/20 flex justify-center items-center gap-4 disabled:opacity-30 transform active:scale-[0.98]"
              >
                {isAnalyzing ? (
                  <div className="flex items-center gap-3"><Loader2 className="animate-spin" /> <span className="uppercase tracking-widest text-sm">{texts.analyzing[language]}</span></div>
                ) : (
                  <><Sparkles size={24} /> <span className="uppercase tracking-widest text-sm">{texts.analyze_btn[language]}</span></>
                )}
              </button>
            </div>
          ) : (
            <div className="space-y-12 animate-in fade-in slide-in-from-top-4 pb-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-violet-50 p-8 rounded-[2.5rem] border border-violet-100 relative overflow-hidden group">
                  <div className="absolute top-0 right-0 p-6 opacity-10"><AlertTriangle size={48} /></div>
                  <h4 className="text-[10px] font-black text-violet-500 uppercase tracking-widest mb-3 flex items-center">{texts.tech_view[language]}</h4>
                  <p className="font-black text-slate-900 text-xl leading-tight">{diagnosis.likelyIssue}</p>
                </div>
                <div className="bg-emerald-50 p-8 rounded-[2.5rem] border border-emerald-100 relative overflow-hidden group">
                  <div className="absolute top-0 right-0 p-6 opacity-10"><DollarSign size={48} /></div>
                  <h4 className="text-[10px] font-black text-emerald-500 uppercase tracking-widest mb-3 flex items-center">{texts.estimate[language]}</h4>
                  <p className="font-black text-slate-900 text-xl">{diagnosis.estimatedPriceRange}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div className="space-y-10">
                  <section className="space-y-4">
                    <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest flex items-center gap-2 px-1">
                      <MapPin size={16} className="text-violet-600" /> {texts.location[language]} <span className="text-rose-500 ml-0.5">*</span>
                    </h3>
                    <div className="flex gap-2 p-1.5 bg-slate-50 rounded-2xl border border-slate-100">
                      <button onClick={() => { setLocationMode('GPS'); setLocation(null); }} className={`flex-1 py-3 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${locationMode === 'GPS' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-400'}`}>{texts.use_gps[language]}</button>
                      <button onClick={() => { setLocationMode('URL'); setLocation(null); }} className={`flex-1 py-3 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${locationMode === 'URL' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-400'}`}>{texts.paste_url[language]}</button>
                    </div>

                    {!location ? (
                      locationMode === 'GPS' ? (
                        <button onClick={handleGetLocation} className="w-full p-6 bg-slate-50 rounded-3xl border-2 border-dashed border-slate-200 text-slate-400 font-black text-xs uppercase tracking-widest hover:bg-slate-100 flex flex-col items-center gap-2">
                           {isGettingLocation ? texts.finding_signal[language] : texts.set_gps[language]}
                        </button>
                      ) : (
                        <div className="space-y-3">
                          <input type="text" value={mapUrlInput} onChange={(e) => setMapUrlInput(e.target.value)} placeholder={texts.map_placeholder[language]} className="w-full p-4 rounded-2xl bg-slate-50 font-bold text-slate-900 outline-none focus:ring-4 focus:ring-violet-100 text-sm" />
                          <button onClick={handleUrlSubmit} className="w-full py-4 bg-slate-900 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest">{texts.verify_link[language]}</button>
                        </div>
                      )
                    ) : (
                      <div className="p-6 bg-emerald-50 rounded-3xl border border-emerald-100 flex items-center justify-between shadow-sm">
                        <span className="font-black text-emerald-700 text-[10px] uppercase">{location.mapUrl ? 'Map Link' : 'GPS Set'}</span>
                        <button type="button" onClick={() => setLocation(null)} className="text-[10px] font-black text-emerald-600 hover:underline">Change</button>
                      </div>
                    )}
                    {locationError && <p className="text-red-500 text-[10px] font-black uppercase ml-1">{locationError}</p>}
                  </section>

                  <section className="space-y-4">
                    <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest flex items-center gap-2 px-1">
                      <Phone size={16} className="text-violet-600" /> {texts.contact[language]} <span className="text-rose-500 ml-0.5">*</span>
                    </h3>
                    <div className="flex gap-3 bg-slate-50 p-2 rounded-2xl">
                      {(['Phone', 'Telegram'] as const).map((m) => (
                        <button key={m} onClick={() => setContactMethod(m)} className={`flex-1 p-4 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all ${contactMethod === m ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-400'}`}>
                          {m === 'Phone' ? texts.phone[language] : 'Telegram'}
                        </button>
                      ))}
                    </div>
                    <input type="text" value={contactValue} onChange={(e) => setContactValue(e.target.value)} placeholder={contactMethod === 'Phone' ? '+855 ...' : '@username'} className="w-full p-5 rounded-[1.8rem] bg-slate-50 font-black text-slate-900 outline-none shadow-inner" />
                  </section>
                </div>

                <section className="space-y-4">
                  <div className="flex justify-between items-center px-1">
                    <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest flex items-center gap-2">
                      <Clock size={16} className="text-violet-600" /> {texts.time[language]} <span className="text-rose-500 ml-0.5">*</span>
                    </h3>
                    {preferredSlots.length < 3 && <button onClick={addSlot} className="text-violet-600 hover:bg-violet-50 p-2 rounded-xl transition-all text-[10px] font-black uppercase"><Plus size={16} /></button>}
                  </div>
                  <div className="space-y-3">
                    {preferredSlots.map((slot, i) => (
                      <div key={i} className="flex gap-2">
                        <input type="datetime-local" value={slot} onChange={(e) => updateSlot(i, e.target.value)} className="w-full pl-6 pr-6 py-5 rounded-[1.8rem] bg-slate-50 font-black text-slate-900 outline-none appearance-none shadow-inner text-sm" />
                        {preferredSlots.length > 1 && <button onClick={() => removeSlot(i)} className="p-5 bg-red-50 text-red-500 rounded-2xl"><Trash2 size={20} /></button>}
                      </div>
                    ))}
                  </div>
                </section>
              </div>

              <div className="pt-8 border-t border-slate-50">
                <button onClick={() => isFormReady && onConfirmBooking({ description, diagnosis, location, contactMethod, contactValue, preferredSlots: preferredSlots.filter(s => s) })} disabled={!isFormReady} className="w-full py-8 bg-slate-900 text-white rounded-[2.5rem] font-black text-xl hover:bg-violet-600 transition-all shadow-2xl disabled:opacity-30 flex items-center justify-center uppercase tracking-widest"><Calendar className="mr-4" size={28} /> {texts.confirm[language]}</button>
              </div>
              <button onClick={() => setDiagnosis(null)} className="w-full py-4 text-slate-400 font-black uppercase text-[10px] hover:text-slate-600">{texts.back[language]}</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};